import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import SectionEditor from "./SectionEditor";

// ============================================================================
// SORTABLE SECTION WRAPPER
// ============================================================================

function SortableSection({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 0,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {React.cloneElement(children, { dragHandleProps: listeners })}
    </div>
  );
}

// ============================================================================
// BUILDER PANEL
// ============================================================================

export default function BuilderPanel({
  config,
  onChange,
  dataSources,
  sectionTemplates = [],
  onSaveAsTemplate,
  onLoadTemplate,
  onDeleteTemplate,
  onRefreshDataSources,
}) {
  const [showAddDropdown, setShowAddDropdown] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  const sections = config?.sections || [];
  const sectionIds = sections.map((section) => section.id);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleSectionChange = (index, updatedSection) => {
    const newSections = [...sections];
    newSections[index] = updatedSection;
    onChange({ ...config, sections: newSections });
  };

  const handleSectionRemove = (index) => {
    const newSections = sections.filter((_, idx) => idx !== index);
    onChange({ ...config, sections: newSections });
  };

  const handleAddSection = () => {
    const newSection = {
      id: `section_${Date.now()}`,
      title: "새 섹션",
      icon: "Settings",
      widgets: [],
    };
    const newSections = [...sections, newSection];
    onChange({ ...config, sections: newSections });
    setShowAddDropdown(false);
  };

  const handleOpenTemplatePicker = () => {
    setShowAddDropdown(false);
    setShowTemplatePicker(true);
  };

  const handleTemplateLoad = (templateId) => {
    if (onLoadTemplate) {
      onLoadTemplate(templateId);
      setShowTemplatePicker(false);
    }
  };

  const handleTemplateDelete = (e, templateId) => {
    e.stopPropagation();
    if (window.confirm("정말 이 템플릿을 삭제하시겠습니까?")) {
      if (onDeleteTemplate) {
        onDeleteTemplate(templateId);
      }
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sections.findIndex((section) => section.id === active.id);
    const newIndex = sections.findIndex((section) => section.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const reordered = arrayMove(sections, oldIndex, newIndex);
      onChange({ ...config, sections: reordered });
    }
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="h-full flex flex-col bg-slate-950">
      {/* HEADER */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
        <div className="flex items-center gap-2">
          <h3 className="text-base font-bold text-slate-200">
            섹션 ({sections.length})
          </h3>
          {onRefreshDataSources && (
            <button
              onClick={onRefreshDataSources}
              className="p-1 text-slate-500 hover:text-indigo-400 transition-colors"
              title="데이터 소스 새로고침"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          )}
        </div>
        <div className="relative">
          <button
            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-1"
            onClick={() => setShowAddDropdown(!showAddDropdown)}
          >
            <span>+ 섹션 추가</span>
            <svg
              className={`w-3 h-3 transition-transform ${
                showAddDropdown ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          {/* Add Dropdown */}
          {showAddDropdown && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowAddDropdown(false)}
              />
              <div className="absolute right-0 top-full mt-1 w-40 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-20 py-1 overflow-hidden">
                <button
                  className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                  onClick={handleAddSection}
                >
                  빈 섹션
                </button>
                <button
                  className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                  onClick={handleOpenTemplatePicker}
                >
                  템플릿에서 추가
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Template Picker Modal */}
      {showTemplatePicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-slate-800 rounded-xl shadow-2xl w-full max-w-md border border-slate-700 max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700">
              <h3 className="text-lg font-bold text-slate-100">
                섹션 템플릿 선택
              </h3>
              <button
                onClick={() => setShowTemplatePicker(false)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 overflow-y-auto flex-1 custom-scrollbar">
              {sectionTemplates && sectionTemplates.length > 0 ? (
                <div className="space-y-3">
                  {sectionTemplates.map((template) => (
                    <div
                      key={template.template_id}
                      className="p-3 bg-slate-900/50 border border-slate-700 rounded-lg hover:border-slate-600 transition-colors group"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h4 className="font-bold text-slate-200 text-sm">
                            {template.template_name}
                          </h4>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {new Date(template.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <button
                          onClick={(e) =>
                            handleTemplateDelete(e, template.template_id)
                          }
                          className="text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all p-1"
                          title="템플릿 삭제"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        </button>
                      </div>
                      {template.description && (
                        <p className="text-xs text-slate-400 mb-3 line-clamp-2">
                          {template.description}
                        </p>
                      )}
                      <button
                        onClick={() => handleTemplateLoad(template.template_id)}
                        className="w-full py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded transition-colors"
                      >
                        추가하기
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-10 text-slate-500 space-y-3">
                  <svg
                    className="w-12 h-12 opacity-50"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                  <p className="text-sm">저장된 템플릿이 없습니다</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* BODY */}
      <div
        className="flex-1 min-h-0 overflow-y-auto p-4 pb-24 space-y-4
          [&::-webkit-scrollbar]:w-1.5
          [&::-webkit-scrollbar-track]:bg-slate-900
          [&::-webkit-scrollbar-thumb]:bg-slate-700
          [&::-webkit-scrollbar-thumb]:rounded-full
          hover:[&::-webkit-scrollbar-thumb]:bg-slate-600"
      >
        {sections.length === 0 ? (
          /* EMPTY STATE */
          <div className="flex flex-col items-center justify-center h-full text-slate-500 space-y-4">
            <svg
              className="w-16 h-16"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 4v16m8-8H4"
              />
            </svg>
            <p className="text-sm text-center max-w-xs">
              섹션을 추가하여 패널 레이아웃을 구성하세요
            </p>
            <button
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold rounded-lg transition-colors"
              onClick={handleAddSection}
            >
              + 첫 섹션 추가
            </button>
          </div>
        ) : (
          /* SECTIONS LIST WITH DND */
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sectionIds}
              strategy={verticalListSortingStrategy}
            >
              {sections.map((section, index) => (
                <SortableSection key={index} id={section.id}>
                  <SectionEditor
                    section={section}
                    sectionIndex={index}
                    onChange={(updatedSection) =>
                      handleSectionChange(index, updatedSection)
                    }
                    onRemove={() => handleSectionRemove(index)}
                    dataSources={dataSources}
                    onSaveAsTemplate={onSaveAsTemplate}
                  />
                </SortableSection>
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}
